import { useEffect, useMemo, useRef } from 'react';

/**
 * Tracks whether the current form state has drifted from the initial snapshot
 * captured when the sheet was opened. Used by EditLoadSheet / EditExportSheet
 * to show a visible "● Unsaved changes" badge and to prompt before close.
 *
 * The comparison is shallow + JSON-string based: the sheet form data is
 * flat and small (< 100 keys), so the cost is negligible and we avoid the
 * trap of per-key deep equality with mixed Date / null / number types.
 *
 * Perf: a single useMemo stringifies the current formData once per change,
 * then both the baseline-capture effect and the dirty calculation reuse it.
 * (Earlier draft did two JSON.stringify passes per render — fine in practice
 * but obviously wasteful.)
 *
 * @param {object}  formData  Current form snapshot.
 * @param {boolean} ready     Pass `false` to ignore changes (e.g. while the
 *                            initial fetch is still resolving). When ready
 *                            flips true the *next* formData becomes the
 *                            baseline.
 * @returns {boolean} dirty   true ⇢ user has unsaved changes.
 */
export const useUnsavedChanges = (formData, ready = true) => {
  // Captures the first non-empty formData we see. Resets when the sheet is
  // closed (parent re-mounts the component with empty formData ⇢ ready=false).
  const baselineRef = useRef(null);

  // Single stringify per change — reused by both the baseline-capture effect
  // and the dirty computation below.
  const currentJson = useMemo(() => {
    if (!formData || Object.keys(formData).length === 0) return '';
    return JSON.stringify(formData);
  }, [formData]);

  useEffect(() => {
    if (!ready) {
      baselineRef.current = null;
      return;
    }
    if (baselineRef.current === null && currentJson) {
      baselineRef.current = currentJson;
    }
  }, [ready, currentJson]);

  return useMemo(() => {
    if (!ready || baselineRef.current === null) return false;
    return currentJson !== baselineRef.current;
  }, [currentJson, ready]);
};

/**
 * Provides a stable click handler the sheet header can wire to its "Exit"
 * button. When `dirty` is true the user gets a native confirm prompt; "OK"
 * closes the sheet, "Cancel" leaves them in place.
 *
 * Native confirm() is intentional: it's modal, accessible by default, and
 * doesn't compete for z-index with the Sheet portal. Custom alert dialogs
 * inside a Sheet are notorious for focus-trap fights.
 */
export const buildConfirmClose = (dirty, onClose) => () => {
  if (dirty) {
    const ok = window.confirm(
      'You have unsaved changes. Close anyway? Your edits will be lost.',
    );
    if (!ok) return;
  }
  onClose();
};
